import { Database, Json } from "../types/supabase";
import { BBox } from "../types/tracking";

export const retroCompat = (
  videoVariant: Database["public"]["Tables"]["videos_variants"]["Row"]
) => {
  // Ensure that the cuts are in the correct format (retro compatibility)
  if (!videoVariant?.cuts) return videoVariant;
  videoVariant.cuts = (videoVariant?.cuts as Json[])?.map((cut: any) => {
    return { start: cut.start ?? cut[0], end: cut.end ?? cut[1] };
  });

  return videoVariant;
};

export const isVideoSchemaV1 = (
  video: Partial<Database["public"]["Tables"]["videos"]["Row"]>
) => {
  return video.status == "ready" && video.src_fps === null;
};

let video: HTMLVideoElement | null = null;
const frameRequests: {
  src: string;
  frame: number;
  bbox?: BBox;
  ratio?: string;
  fps: number;
  resolve: (data: string) => void;
  reject: (reason: any) => void;
}[] = [];

export function initializeVideoElement() {
  video = document.createElement("video");
  video.style.display = "none";
  video.crossOrigin = "anonymous";
  document.body.appendChild(video);

  video.addEventListener("loadeddata", handleLoadedData);
}

function handleLoadedData() {
  if (!video) return;

  const request = frameRequests.shift()!;
  let [xRel, yRel, widthRel, heightRel] = request.bbox || [0, 0, 1, 1];

  if (request.ratio) {
    const [rw, rh] = request.ratio.split(":").map(Number);
    const originalAspect = widthRel / heightRel;
    const desiredAspect = rw / rh;

    if (desiredAspect > originalAspect) {
      // Width becomes dominant
      const newHeight = widthRel / desiredAspect;
      yRel += (heightRel - newHeight) / 2;
      heightRel = newHeight;
    } else {
      // Height becomes dominant
      const newWidth = heightRel * desiredAspect;
      xRel += (widthRel - newWidth) / 2;
      widthRel = newWidth;
    }
  }

  const xAbs = (video.videoWidth * xRel) / 2;
  const yAbs = (video.videoHeight * yRel) / 2;
  const widthAbs = video.videoWidth * widthRel;
  const heightAbs = video.videoHeight * heightRel;

  // Adjust canvas dimensions based on the requested ratio
  let canvasWidth, canvasHeight;

  if (request.ratio === "1:1") {
    canvasWidth = canvasHeight = Math.max(widthAbs, heightAbs);
  } else {
    canvasWidth = widthAbs;
    canvasHeight = heightAbs;
  }

  const canvas = document.createElement("canvas");
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const ctx = canvas.getContext("2d");
  if (ctx) {
    // Draw the cropped image centered on the canvas
    ctx.drawImage(
      video,
      xAbs,
      yAbs,
      widthAbs,
      heightAbs,
      (canvasWidth - widthAbs) / 2,
      (canvasHeight - heightAbs) / 2,
      widthAbs,
      heightAbs
    );
  }

  request.resolve(canvas.toDataURL());
  processNextRequest();
}

function processNextRequest() {
  if (frameRequests.length === 0) return;

  const nextRequest = frameRequests[0];
  if (!video) return;

  video.src = nextRequest.src;
  if (!nextRequest.frame) return;
  if (!nextRequest.fps) return;
  video.currentTime = nextRequest.frame / nextRequest.fps; // Assuming 30 fps
}

export function requestFrame(
  src: string,
  frame: number,
  fps: number,
  bbox?: BBox,
  ratio?: string
): Promise<string> {
  return new Promise((resolve, reject) => {
    frameRequests.push({ src, frame, bbox, ratio, fps, resolve, reject });
    if (frameRequests.length === 1) {
      processNextRequest();
    }
  });
}
